import React, { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Play, Send } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";

export const VideoCard = ({ video, whatsappNumber, index = 0 }) => {
  const [playing, setPlaying] = useState(false);
  const defaultMessage = `I want this invite as given in sample with name: "${video.title}" (${video.category}). Please share the details.`;
  const [message, setMessage] = useState(defaultMessage);
  const [previewOpen, setPreviewOpen] = useState(false);

  const sendWhatsApp = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`, "_blank");
    setPreviewOpen(false);
  };

  const isPortrait = video.orientation === "portrait";
  const aspectClass = isPortrait ? "aspect-[9/16]" : "aspect-video";
  const embedSrc =
    video.provider === "vimeo"
      ? `https://player.vimeo.com/video/${video.video_id}?autoplay=1&title=0&byline=0&portrait=0`
      : `https://www.youtube.com/embed/${video.video_id}?autoplay=1&rel=0`;
  const thumb = video.thumbnail;

  return (
    <motion.div
      data-testid={`video-card-${video.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 6) * 0.06 }}
      className="group bg-white rounded-2xl overflow-hidden border border-amber-500/25 shadow-[0_4px_20px_-4px_rgba(212,175,55,0.15)] hover:border-amber-500/60 hover:shadow-[0_12px_36px_-6px_rgba(212,175,55,0.35)] hover:-translate-y-1 transition-all duration-300"
    >
      <div
        className={`relative ${aspectClass} bg-stone-900 ${isPortrait ? "max-h-[560px] mx-auto w-full" : ""}`}
        data-testid={`youtube-player-${video.id}`}
      >
        {playing ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={embedSrc}
            title={video.title}
            frameBorder="0"
            allow="accelerometer; autoplay; fullscreen; encrypted-media; picture-in-picture; clipboard-write"
            allowFullScreen
          />
        ) : (
          <button
            data-testid={`play-video-${video.id}`}
            onClick={() => setPlaying(true)}
            className="absolute inset-0 w-full h-full group/play"
          >
            {thumb ? (
              <img
                src={thumb}
                alt={video.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
              />
            ) : (
              <span className="absolute inset-0 bg-gradient-to-br from-amber-700 via-amber-500 to-amber-800" />
            )}
            <span className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
            {video.provider === "vimeo" && (
              <span className="absolute top-3 left-3 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-black/50 text-white backdrop-blur">
                3D · Vimeo
              </span>
            )}
          </button>
        )}
      </div>

      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide bg-amber-100 text-amber-900 border border-amber-300/60">
            {video.category}
          </span>
        </div>
        <h3
          data-testid={`video-title-${video.id}`}
          className="mt-3 font-serif text-xl font-semibold text-amber-950 leading-snug line-clamp-2"
        >
          {video.title}
        </h3>

        <Popover open={previewOpen} onOpenChange={(o) => { setPreviewOpen(o); if (o) setMessage(defaultMessage); }}>
          <PopoverTrigger asChild>
            <button
              data-testid={`whatsapp-order-btn-${video.id}`}
              className="shimmer-btn mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] hover:bg-[#1EBE5B] text-white font-semibold py-3 px-4 shadow-md hover:shadow-emerald-500/30 transition-all duration-300 active:scale-[0.98]"
            >
              <MessageCircle className="h-5 w-5" />
              I want this invite
            </button>
          </PopoverTrigger>
          <PopoverContent data-testid={`whatsapp-preview-${video.id}`} className="w-80 bg-white border-amber-500/30" align="center">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 flex items-center gap-1.5">
              <MessageCircle className="h-3.5 w-3.5 text-emerald-600" /> Message preview
            </p>
            <p className="mt-1 text-[11px] text-stone-500">Edit the wording if you like, then send.</p>
            <Textarea
              data-testid={`whatsapp-message-input-${video.id}`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="mt-2 text-sm bg-[#FAF6F0] border-amber-500/30 focus-visible:ring-amber-500/40"
            />
            <button
              data-testid={`whatsapp-send-btn-${video.id}`}
              onClick={sendWhatsApp}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] hover:bg-[#1EBE5B] text-white font-semibold py-2.5 px-4 transition-colors"
            >
              <Send className="h-4 w-4" /> Send on WhatsApp
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </motion.div>
  );
};
